
import { Calendar, MapPin, Users, Tag, Check } from 'lucide-react';
import HeroSection from '@/components/ui-custom/HeroSection';
import RallyCard from '@/components/ui-custom/RallyCard';
import { NavLink } from 'react-router-dom';
import { useFeaturedRally } from '@/hooks/useFeaturedRally';

const Rallies = () => {
  const { rally, loading, error } = useFeaturedRally();

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Extract location from meeting_location array
  const getLocation = (location: [number, number]) => {
    // This is a placeholder - in a real app you'd reverse geocode or store location name
    return "Location TBD";
  };

  // Get lowest ticket price
  const getLowestPrice = (ticketTiers?: any[]) => {
    if (!ticketTiers || ticketTiers.length === 0) return null;
    const prices = ticketTiers.map(tier => tier.price);
    return Math.min(...prices);
  };

  return (
    <div className="bg-adv-dark min-h-screen">
      <HeroSection
        title="Exodus ADV Rallies"
        subtitle="Multi-day adventure motorcycle events that combine epic riding, camping, and community."
        backgroundImage="/images/rallies-hero.jpg"
      />
      
      <main id="content" className="pb-20">
        {/* About Rally Series */}
        <section className="section-padding">
          <div className="container-custom">
            <div className="text-center mb-12">
              <h2 className="heading-lg text-white mb-4">The Rally Experience</h2>
              <p className="text-adv-lightGray max-w-2xl mx-auto">
                Our rallies are the cornerstone of the Exodus ADV community. Held twice a year in select locations, they offer the perfect blend of adventure, camaraderie, and exploration.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-12">
              <div className="glass-card rounded-lg p-6 text-center">
                <div className="w-12 h-12 bg-adv-orange/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="text-adv-orange" size={24} />
                </div>
                <h3 className="font-bold text-lg mb-2">Epic Locations</h3>
                <p className="text-adv-lightGray">
                  Handpicked destinations with access to diverse terrain and breathtaking scenery.
                </p>
              </div>
              
              <div className="glass-card rounded-lg p-6 text-center">
                <div className="w-12 h-12 bg-adv-orange/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="text-adv-orange" size={24} />
                </div>
                <h3 className="font-bold text-lg mb-2">Community</h3>
                <p className="text-adv-lightGray">
                  Connect with fellow riders, share stories around the campfire, and build lasting friendships.
                </p>
              </div>
              
              <div className="glass-card rounded-lg p-6 text-center">
                <div className="w-12 h-12 bg-adv-orange/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar className="text-adv-orange" size={24} />
                </div>
                <h3 className="font-bold text-lg mb-2">Real Adventure</h3>
                <p className="text-adv-lightGray">
                  Exodus ADV Rallies don't stay in one place. We move each day from camp to camp, broken into smaller groups, creating a lasting memory and a shared experience.
                </p>
              </div>
            </div>
          </div>
        </section>
        
        {/* Highlighted Upcoming Rally */}
        <section className="section-padding bg-adv-charcoal border-y border-adv-darkGray">
          <div className="container-custom">
            {loading ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="mb-4">
                    <div className="w-20 h-6 bg-gray-600 rounded-full mb-3 animate-pulse"></div>
                    <div className="w-64 h-8 bg-gray-600 rounded mb-2 animate-pulse"></div>
                    <div className="w-96 h-4 bg-gray-600 rounded animate-pulse"></div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                    {Array(4).fill(0).map((_, i) => (
                      <div key={i} className="flex items-center">
                        <div className="w-5 h-5 bg-gray-600 rounded mr-2 animate-pulse"></div>
                        <div className="w-32 h-4 bg-gray-600 rounded animate-pulse"></div>
                      </div>
                    ))}
                  </div>
                  <div className="w-64 h-4 bg-gray-600 rounded mb-8 animate-pulse"></div>
                  <div className="w-32 h-10 bg-gray-600 rounded animate-pulse"></div>
                </div>
                <div className="rounded-lg overflow-hidden shadow-lg">
                  <div className="w-full h-64 bg-gray-600 animate-pulse"></div>
                </div>
              </div>
            ) : error || !rally ? (
              // Placeholder when no featured rallies are available
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="mb-4">
                    <span className="inline-block px-3 py-1 bg-adv-orange text-white text-xs font-medium rounded-full mb-3">
                      Coming Soon
                    </span>
                    <h2 className="heading-lg text-white mb-2">Organize Your Own Rally</h2>
                    <p className="text-adv-lightGray">
                      We're working on bringing you the ability to create and organize your own adventure rallies. Soon you'll be able to plan epic multi-day events and bring the community together.
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                    <div className="flex items-center">
                      <Calendar size={20} className="text-adv-orange mr-2" />
                      <span className="text-white">Custom Dates</span>
                    </div>
                    <div className="flex items-center">
                      <MapPin size={20} className="text-adv-orange mr-2" />
                      <span className="text-white">Your Location</span>
                    </div>
                    <div className="flex items-center">
                      <Users size={20} className="text-adv-orange mr-2" />
                      <span className="text-white">Community Driven</span>
                    </div>
                    <div className="flex items-center">
                      <Tag size={20} className="text-adv-orange mr-2" />
                      <span className="text-white">Flexible Pricing</span>
                    </div>
                  </div>
                  
                  <p className="text-adv-lightGray mb-8">
                    Our platform will soon allow you to create rally events, set up ticket tiers, manage registrations, and coordinate with fellow riders. Be the first to know when this feature launches.
                  </p>
                  
                  <button className="text-white btn-primary" disabled>
                    Coming Soon
                  </button>
                </div>
              </div>
            ) : (
              // Actual featured rally data
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="mb-4">
                    <span className="inline-block px-3 py-1 bg-adv-orange text-white text-xs font-medium rounded-full mb-3">
                      Next Rally
                    </span>
                    <h2 className="heading-lg text-white mb-2">{rally.title}</h2>
                    <p className="text-adv-lightGray">
                      {rally.description}
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                    <div className="flex items-center">
                      <Calendar size={20} className="text-adv-orange mr-2" />
                      <span className="text-white">{formatDate(rally.meeting_datetime)}</span>
                    </div>
                    <div className="flex items-center">
                      <MapPin size={20} className="text-adv-orange mr-2" />
                      <span className="text-white">{getLocation(rally.meeting_location)}</span>
                    </div>
                    <div className="flex items-center">
                      <Users size={20} className="text-adv-orange mr-2" />
                      <span className="text-white">Limited spots available</span>
                    </div>
                    <div className="flex items-center">
                      <Tag size={20} className="text-adv-orange mr-2" />
                      <span className="text-white">
                        {getLowestPrice(rally.ticket_tiers) ? `From $${getLowestPrice(rally.ticket_tiers)}` : 'Pricing TBD'}
                      </span>
                    </div>
                  </div>
                  
                  <p className="text-adv-lightGray mb-8">
                    Join us for an epic adventure with fellow riders. Experience the best trails, group campfires, skills workshops, and more. All adventure enthusiasts are welcome.
                  </p>
                  
                  <NavLink to={`/rallies/${rally.id}`} className="btn-primary">
                    Register Now
                  </NavLink>
                </div>
                
                <div className="rounded-lg overflow-hidden shadow-lg">
                  <img 
                    src={rally.image_url || "/images/rally-placeholder.jpg"} 
                    alt={rally.title} 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            )}
          </div>
        </section>
        
        {/* Registration Options - Only show if there's a featured rally */}
        {rally && rally.ticket_tiers && rally.ticket_tiers.length > 0 && (
          <section className="section-padding">
            <div className="container-custom">
              <div className="text-center mb-12">
                <h2 className="heading-lg text-white mb-4">Registration Options</h2>
                <p className="text-adv-lightGray max-w-2xl mx-auto">
                  Choose the rally experience that suits your style.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                {rally.ticket_tiers.map((tier, index) => (
                  <div 
                    key={index}
                    className={`glass-card rounded-lg p-6 md:p-8 border ${
                      index === 0 ? 'border-adv-darkGray/50' : 'border-adv-orange/30 bg-adv-orange/5'
                    }`}
                  >
                    <div className="flex justify-between items-center mb-6">
                      <h3 className="text-xl font-bold text-white">{tier.name}</h3>
                      <span className="text-2xl font-bold text-adv-orange">${tier.price}</span>
                    </div>
                    
                    <div className="mb-8">
                      <p className="text-adv-lightGray">{tier.description}</p>
                    </div>
                    
                    <NavLink 
                      to={`/rallies/${rally.id}?package=${tier.name.toLowerCase().replace(/\s+/g, '-')}`} 
                      className={`w-full text-center ${
                        index === 0 ? 'btn-secondary' : 'btn-primary'
                      }`}
                    >
                      Select {tier.name}
                    </NavLink>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}
        
        {/* Past Rallies */}
        <section className="section-padding bg-adv-charcoal border-y border-adv-darkGray">
          <div className="container-custom">
            <div className="text-center mb-12">
              <h2 className="heading-lg text-white mb-4">Past Rallies</h2>
              <p className="text-adv-lightGray max-w-2xl mx-auto">
                Relive the memories from our previous adventures.
              </p>
            </div>
            
            <div className="text-center py-12">
              <div className="max-w-md mx-auto">
                <div className="w-16 h-16 bg-adv-orange/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Calendar className="text-adv-orange" size={32} />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">Past Rallies Coming Soon</h3>
                <p className="text-adv-lightGray mb-6">
                  We're working on bringing you a complete history of our past rallies, including photos, stories, and memories from previous adventures.
                </p>
                <div className="inline-flex items-center px-4 py-2 bg-adv-orange/10 border border-adv-orange/20 rounded-full">
                  <span className="text-adv-orange text-sm font-medium">Feature in Development</span>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* FAQ Section */}
        <section className="section-padding">
          <div className="container-custom">
            <div className="text-center mb-12">
              <h2 className="heading-lg text-white mb-4">Frequently Asked Questions</h2>
              <p className="text-adv-lightGray max-w-2xl mx-auto">
                Everything you need to know about our rallies.
              </p>
            </div>
            
            <div className="max-w-3xl mx-auto space-y-4">
              <div className="glass-card rounded-lg p-5">
                <h3 className="text-white font-bold mb-2">What skill level is required for the rallies?</h3>
                <p className="text-adv-lightGray">
                  We highly recommend some level of off-road experience on a bike similar to the one you will attend with. As “adventure” rallies, you can expect a mix of on and off-road, with optional harder sections along the way. There will always be bypasses for sections deemed more difficult.
                </p>
              </div>
              
              <div className="glass-card rounded-lg p-5">
                <h3 className="text-white font-bold mb-2">What type of motorcycle is suitable?</h3>
                <p className="text-adv-lightGray">
                  Street legal adventure or dual sport motorcycles are ideal. Most ExodusADV Rally routes will be geared towards the mid-size ADV bike category (400-500lbs). Some would rather ride smaller bikes for this, and some are comfortable on bigger bikes.
                </p>
              </div>
              
              <div className="glass-card rounded-lg p-5">
                <h3 className="text-white font-bold mb-2">What's included in the meals?</h3>
                <p className="text-adv-lightGray">
                  Depending on the rally, official ExodusADV Rallies will partner with either local providers or an “FOE” (Friend of Exodus). Meal details will be provided on the official rally page.
                </p>
              </div>
              
              <div className="glass-card rounded-lg p-5">
                <h3 className="text-white font-bold mb-2">Is there cell service at the rally locations?</h3>
                <p className="text-adv-lightGray">
                  Cell service varies by location. While we recommend some way to contact your loved ones or emergency services (satellite communication devices like an inReach or some newer phones), all ExodusADV group leaders will have these devices.
                </p>
              </div>
              
              <div className="glass-card rounded-lg p-5">
                <h3 className="text-white font-bold mb-2">What should I bring?</h3>
                <p className="text-adv-lightGray">
                  Depending on the ticket you purchase, you may only need to bring your bike and riding gear, or you may need the full “adv” loadout. Check our packing list for ideas and requirements!
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Rallies;
